/**
 * Data Loader
 * 
 * Fetches property data from the Multihub GraphQL API.
 */

import type { Property } from '@vibesites/schema';
import { buildPropertyQuery } from '@vibesites/schema';
import { normalizePropertyPaths } from './slug-utils';

// ============================================================================
// Types
// ============================================================================

export interface DataLoaderConfig {
  /** GraphQL endpoint URL */
  endpoint: string;
  /** Bearer token for authentication */
  token: string;
  /** Property ID to fetch */
  propertyId: string;
}

export interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string; path?: string[] }>;
}

export interface PropertyQueryResult {
  properties: Property | Property[];
}

// ============================================================================
// GraphQL Client
// ============================================================================

/**
 * Execute a GraphQL query against the Multihub API.
 */
export async function executeQuery<T>(
  config: DataLoaderConfig,
  query: string
): Promise<T> {
  const response = await fetch(config.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${config.token}`,
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    const error = new Error(`GraphQL request failed: ${response.status} ${response.statusText}`) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  const result: GraphQLResponse<T> = await response.json();

  if (result.errors && result.errors.length > 0) {
    const errorMessages = result.errors.map(e => e.message).join(', ');
    throw new Error(`GraphQL errors: ${errorMessages}`);
  }

  if (!result.data) {
    throw new Error('No data returned from GraphQL query');
  }

  return result.data;
}

// ============================================================================
// Custom Fields Flattening
// ============================================================================

/**
 * Flatten custom_fields array into top-level property keys.
 * 
 * GraphQL returns custom fields as:
 *   custom_fields: [{ ID: "custom_foo", type: "STRING", value: "bar" }]
 * 
 * This transforms them into:
 *   property["custom_foo"] = "bar"
 * 
 * This allows custom fields to be used seamlessly with the existing
 * FIELD_MAPPING override system — no changes needed to content resolution,
 * slot groups, or design tokens.
 * 
 * The original custom_fields array is preserved for reference by the
 * field picker UI (to show type metadata and group label).
 */
function flattenCustomFields(property: Property): Property {
  if (!property.custom_fields?.length) return property;

  const result = { ...property };
  for (const cf of property.custom_fields) {
    if (cf.ID && cf.value != null) {
      // Only flatten if the key doesn't already exist on the property
      // to avoid overwriting standard fields
      if (!(cf.ID in result)) {
        (result as Record<string, unknown>)[cf.ID] = cf.value;
      }
    }
  }
  return result;
}

// ============================================================================
// Property Data Loader
// ============================================================================

/**
 * Load full property data from the GraphQL API.
 * Custom fields are automatically flattened into top-level keys.
 */
export async function loadProperty(config: DataLoaderConfig): Promise<Property> {
  const query = buildPropertyQuery(config.propertyId);
  
  const data = await executeQuery<PropertyQueryResult>(config, query);

  // Handle both array and single object responses
  const properties = Array.isArray(data.properties) ? data.properties : [data.properties];
  
  if (!properties || properties.length === 0) {
    throw new Error(`Property not found: ${config.propertyId}`);
  }

  return normalizePropertyPaths(flattenCustomFields(properties[0]));
}

// ============================================================================
// Caching Layer
// ============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

/**
 * Load property with caching.
 */
export async function loadPropertyCached(
  config: DataLoaderConfig,
  ttl: number = 60000 // 1 minute default
): Promise<Property> {
  const cacheKey = `property:${config.propertyId}`;
  const now = Date.now();

  // Check cache
  const cached = cache.get(cacheKey) as CacheEntry<Property> | undefined;
  if (cached && (now - cached.timestamp) < cached.ttl) {
    return cached.data;
  }

  // Fetch fresh data
  const property = await loadProperty(config);

  // Update cache
  cache.set(cacheKey, {
    data: property,
    timestamp: now,
    ttl,
  });

  return property;
}

/**
 * Clear the data cache.
 */
export function clearCache(): void {
  cache.clear();
}

/**
 * Invalidate a specific property from cache.
 */
export function invalidateProperty(propertyId: string): void {
  cache.delete(`property:${propertyId}`);
}

// ============================================================================
// Static Data Generation
// ============================================================================

/**
 * Fetch all data needed for static site generation.
 */
export async function loadStaticSiteData(config: DataLoaderConfig): Promise<{
  property: Property;
  generatedAt: string;
}> {
  const property = await loadProperty(config);

  return {
    property,
    generatedAt: new Date().toISOString(),
  };
}
